import { useEffect, useState } from "react";
import { Star, Quote } from "lucide-react";
import { REVIEWS } from "@/data/reviews";
import { supabase } from "@/lib/supabase";

function initialsOf(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");
}

export default function Reviews({ hideHeader = false }) {
  const [reviews, setReviews] = useState(REVIEWS); // seed le temps du chargement
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("reviews")
          .select("*")
          .eq("status", "approved")
          .order("created_at", { ascending: false });
        if (!active) return;
        if (error) throw error;
        if (data && data.length > 0) {
          setReviews(
            data.map((r) => ({
              id: r.id,
              author: r.author,
              role: r.role,
              rating: r.rating,
              comment: r.comment,
              source: r.source,
              initials: initialsOf(r.author),
            }))
          );
        }
        // si la base est vide, on garde les avis seed (REVIEWS)
      } catch {
        // hors-ligne / erreur : on garde les avis seed
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <section id="avis" className="relative py-28 lg:py-36 border-t border-white/5 overflow-hidden" data-testid="reviews-section">
      <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[500px] h-[500px] bg-[#E50914]/8 rounded-full blur-[160px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-16">
          {!hideHeader && (
            <>
              <div className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-4">
                / 05 — Témoignages
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-6">
                Ce qu'en disent<br />
                <span className="text-gradient-red">nos clients.</span>
              </h2>
            </>
          )}
          <div className="flex items-center gap-4 text-sm text-white/60">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-[#E50914] text-[#E50914]" />
              ))}
            </div>
            <span className="font-semibold text-white">5,0 / 5</span>
            <span className="text-white/40">— Clients vérifiés</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="reviews-grid">
          {reviews.map((r) => (
            <ReviewCard key={r.id} review={r} />
          ))}
        </div>

        <p className="mt-12 text-center text-xs text-white/40 font-mono">
          // Avis authentiques de nos clients
        </p>
      </div>
    </section>
  );
}

function SourceBadge({ source }) {
  if (source === "google") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/15 text-[10px] font-bold uppercase tracking-wider text-white/70">
        <GoogleG className="w-3 h-3" /> Avis Google
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#E50914]/10 border border-[#E50914]/40 text-[10px] font-bold uppercase tracking-wider text-[#E50914]">
      ✓ Avis site
    </span>
  );
}

function ReviewCard({ review }) {
  return (
    <div
      data-testid={`review-${review.id}`}
      className="group relative p-8 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl hover:border-[#E50914]/40 hover:-translate-y-1 transition-all"
    >
      <Quote className="absolute top-6 right-6 w-8 h-8 text-[#E50914]/20 group-hover:text-[#E50914]/40 transition-colors" />

      <div className="flex items-center gap-1 mb-5">
        {[...Array(review.rating || 5)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-[#E50914] text-[#E50914]" />
        ))}
      </div>

      <p className="text-white/80 leading-relaxed mb-6 text-sm">"{review.comment}"</p>

      <div className="flex items-center justify-between gap-3 pt-5 border-t border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#E50914] to-[#7f0611] flex items-center justify-center font-bold text-white text-sm">
            {review.initials}
          </div>
          <div>
            <div className="font-semibold text-white text-sm">{review.author}</div>
            {review.role && <div className="text-xs text-white/50">{review.role}</div>}
          </div>
        </div>
        <SourceBadge source={review.source} />
      </div>
    </div>
  );
}

function GoogleG({ className }) {
  return (
    <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4-4 5.3l6.3 5.3C41.2 35.9 44 30.4 44 24c0-1.3-.1-2.3-.4-3.5z"/>
    </svg>
  );
}
